import * as React from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/userType";
import { Button } from "./button";
import { useGetBatchQuery } from "@/store/batchApi";
import {
  useStudentTakeInterviewMutation,
  useGradeStudentInterviewMutation,
} from "@/store/instructor/interview";
import { useUpdateRankMutation } from "@/store/studentApi";
import Pagination from "../Pagination";

const InstructorStudentList = ({ students }: { students: any[] }) => {
  const [selectedBatch, setSelectedBatch] = React.useState<string | null>(null);
  const [openStudent, setOpenStudent] = React.useState(null);
  const [form, setForm] = React.useState({
    technicalScore: "",
    communicationScore: "",
    problemSolvingScore: "",
    feedback: "",
    interviewId: "",
  });
  const { user } = useSelector((state: RootState) => state.user);
  const { data: batches } = useGetBatchQuery();
  const [studentTakeInterview, { isLoading: isTaking }] =
    useStudentTakeInterviewMutation();
  const [gradeStudentInterview, { isLoading: isGrading }] =
    useGradeStudentInterviewMutation();
  const [updateRank] = useUpdateRankMutation();

  // Get unique batches for filter buttons
  const uniqueBatches: string[] = Array.from(
    new Set(students.map((student) => String(student.batch))),
  );

  // Filter students by selected batch
  const filteredStudents = selectedBatch
    ? students.filter((student) => student.batch === selectedBatch)
    : students;

  const getRankGrade = (score) => {
    if (!score) return "-";
    if (score >= 90) return "S";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score >= 50) return "D";
    return "F";
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-4">
      {/* Batch Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          onClick={() => setSelectedBatch(null)}
          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
            selectedBatch === null
              ? "bg-red-500 text-white"
              : "bg-gray-100 text-gray-800 hover:bg-gray-200"
          }`}
        >
          All Batches
        </Button>

        {/* <div className="flex flex-wrap gap-2">
          {uniqueBatches.map((batch) => (
            <Button
              key={batch}
              onClick={() => setSelectedBatch(batch)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                selectedBatch === batch
                  ? "bg-red-500 text-white"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              }`}
            >
              {batch}
              <span className="ml-2 bg-white text-red-800 px-2 py-0.5 rounded-full text-xs">
                {students.filter((s) => s.batch === batch).length}
              </span>
            </Button>
          ))}
        </div> */}
        <div className="w-64">
          <select
            value={selectedBatch || ""}
            onChange={(e) => setSelectedBatch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Select Batch</option>

            {uniqueBatches.map((batch) => (
              <option key={batch} value={batch}>
                {batch}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Batch Info */}
      {selectedBatch && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">
            {selectedBatch}
            <span className="ml-2 bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs">
              {filteredStudents.length} students
            </span>
          </h3>
        </div>
      )}

      {/* Upload Rank Modal */}
      {openStudent && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenStudent(null)}
          />
          <div className="bg-white rounded p-6 z-10 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">
              Upload Rank for {openStudent.fname} {openStudent.lname}
            </h3>
            <div className="space-y-2">
              <label className="text-sm">Technical Score (0-100)</label>
              <input
                className="w-full border p-2 rounded"
                type="number"
                value={form.technicalScore}
                onChange={(e) =>
                  setForm({ ...form, technicalScore: e.target.value })
                }
              />
              <label className="text-sm">Communication Score (0-100)</label>
              <input
                className="w-full border p-2 rounded"
                type="number"
                value={form.communicationScore}
                onChange={(e) =>
                  setForm({ ...form, communicationScore: e.target.value })
                }
              />
              <label className="text-sm">Problem Solving Score (0-100)</label>
              <input
                className="w-full border p-2 rounded"
                type="number"
                value={form.problemSolvingScore}
                onChange={(e) =>
                  setForm({ ...form, problemSolvingScore: e.target.value })
                }
              />
              <label className="text-sm">Feedback (optional)</label>
              <textarea
                className="w-full border p-2 rounded"
                value={form.feedback}
                onChange={(e) => setForm({ ...form, feedback: e.target.value })}
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  onClick={() => setOpenStudent(null)}
                  className="bg-gray-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    // determine batch _id
                    const batchObj = (batches || []).find(
                      (b) =>
                        String(b.bCode) === String(openStudent.batch) ||
                        String(b._id) === String(openStudent.batch),
                    );
                    const batchId = batchObj?._id || openStudent.batch;
                    // fetch interviews for the batch to find appropriate interview id (module matching optional)
                    const resp = await fetch(`/api/interview/batch/${batchId}`);
                    const json = await resp.json();
                    const interviews = json?.data || [];

                    // pick interview id (prefer form.interviewId if set)
                    let interviewId =
                      form.interviewId ||
                      interviews.find((i) => i.status === "scheduled")?._id;
                    if (!interviewId) {
                      alert(
                        "No scheduled interview found for this batch. Please schedule an interview first.",
                      );
                      return;
                    }

                    try {
                      // 1) Create a StudentInterview (mark student as agreed/scheduled for this interview)
                      const takePayload = { studentId: openStudent._id };
                      const takeRes = await studentTakeInterview({
                        interviewId,
                        Token: user?.token,
                        formData: takePayload,
                      }).unwrap();
                      const studentInterview = takeRes?.data || takeRes;

                      // 2) Grade the created studentInterview immediately (instructor provides ranks)
                      const gradePayload = {
                        technicalScore: Number(form.technicalScore),
                        communicationScore: Number(form.communicationScore),
                        problemSolvingScore: Number(form.problemSolvingScore),
                        feedback: form.feedback,
                        instructorId: user?._id,
                      };

                      const gradeRes = await gradeStudentInterview({
                        studentInterviewId: studentInterview._id,
                        Token: user?.token,
                        formData: gradePayload,
                      }).unwrap();
                      const graded = gradeRes?.data || gradeRes;

                      // 3) Update student's rank using overallScore from graded attempt
                      const overall =
                        graded?.overallScore ||
                        graded?.data?.overallScore ||
                        null;
                      if (overall !== null && overall !== undefined) {
                        const rank = Math.round(overall / 10);
                        await updateRank({
                          id: openStudent._id,
                          Token: user?.token,
                          rank,
                        }).unwrap();
                      }

                      // Close modal and refresh
                      setOpenStudent(null);
                      setForm({
                        technicalScore: "",
                        communicationScore: "",
                        problemSolvingScore: "",
                        feedback: "",
                        interviewId: "",
                      });
                      window.location.reload();
                    } catch (err) {
                      console.error("Error processing interview", err);
                      alert(
                        err?.data?.message ||
                          err.message ||
                          "Failed to process interview",
                      );
                    }
                  }}
                  disabled={isTaking || isGrading}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Table Section */}
      <Pagination data={filteredStudents} itemsPerPage={10}>
        {(paginationData) => (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      First Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mode
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginationData?.map((student) => (
                    <tr
                      key={student._id}
                      className="hover:bg-muted bg-background transition-colors group"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.fname}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.lname}
                      </td>
                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          student.status === "Active"
                            ? "text-red-600"
                            : "text-red-400"
                        }`}
                      >
                        {student.status}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.isCompleted}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.course}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.mode}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.contact}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student.batch}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {student?.totalScore
                          ? getRankGrade(student.totalScore)
                          : student?.rank || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Pagination>
    </div>
  );
};

export default InstructorStudentList;
